import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Upload, X, Image as ImageIcon, Type } from "lucide-react";

const FIELDS = [
  { group: "Brand", items: [
    { key: "business_name", label: "Business name", type: "text" },
    { key: "business_tagline", label: "Header tagline (small text under logo)", type: "text" },
    { key: "logo_image", label: "Logo image (square, shown in header)", type: "image" },
  ]},
  { group: "Hero — Home page", items: [
    { key: "hero_image", label: "Hero image (large image at top of home page)", type: "image" },
    { key: "hero_eyebrow", label: "Eyebrow text (small line above headline)", type: "text" },
    { key: "hero_title_l1", label: "Headline line 1", type: "text" },
    { key: "hero_title_l2", label: "Headline line 2", type: "text" },
    { key: "hero_title_accent", label: "Headline line 3 (red accent)", type: "text" },
    { key: "hero_paragraph", label: "Paragraph below headline", type: "textarea" },
    { key: "hero_cta_primary", label: "Primary button text", type: "text" },
    { key: "hero_cta_secondary", label: "Secondary button text", type: "text" },
  ]},
  { group: "Contact & Footer", items: [
    { key: "contact_address", label: "Physical address (leave blank to hide)", type: "textarea" },
    { key: "contact_phone", label: "Phone number (leave blank to hide)", type: "text" },
    { key: "contact_email", label: "Contact email", type: "text" },
    { key: "footer_text", label: "Footer subtitle", type: "text" },
  ]},
  { group: "About Us page", items: [
    { key: "about_hero_image", label: "Hero image (top-right of About Us page)", type: "image" },
    { key: "about_eyebrow", label: "Eyebrow (small line above title)", type: "text" },
    { key: "about_title", label: "Page title", type: "text" },
    { key: "about_intro", label: "Intro paragraph", type: "textarea" },
    { key: "about_section1_title", label: "Section 1 title", type: "text" },
    { key: "about_section1_body", label: "Section 1 body", type: "textarea" },
    { key: "about_section1_image", label: "Section 1 image (optional)", type: "image" },
    { key: "about_section2_title", label: "Section 2 title", type: "text" },
    { key: "about_section2_body", label: "Section 2 body", type: "textarea" },
    { key: "about_section2_image", label: "Section 2 image (optional)", type: "image" },
    { key: "about_section3_title", label: "Section 3 title", type: "text" },
    { key: "about_section3_body", label: "Section 3 body", type: "textarea" },
    { key: "about_section3_image", label: "Section 3 image (optional)", type: "image" },
    { key: "about_gallery_image", label: "Featured gallery image (5th picture, displayed full-width)", type: "image" },
    { key: "about_video_url", label: "Video URL — YouTube, Vimeo, or direct .mp4 link", type: "text" },
    { key: "about_quote", label: "Pull quote (callout)", type: "textarea" },
    { key: "about_quote_author", label: "Quote author / attribution", type: "text" },
  ]},
];

export default function SiteContentTab({ API, authHeaders, refreshContent }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then(r => setData(r.data));
  }, [API]);

  if (!data) return <div className="font-mono text-sm">Loading content…</div>;

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const saveAll = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(g => g.items.forEach(it => { payload[it.key] = data[it.key] ?? ""; }));
      await axios.put(`${API}/admin/content`, payload, { headers: authHeaders() });
      toast.success("Site content saved");
      refreshContent && refreshContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="max-w-3xl" data-testid="site-content-tab">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl">SITE CONTENT</h2>
          <p className="font-mono text-xs text-[var(--ink-2)]">Edit any text or image shown on the public site. Changes go live as soon as you save.</p>
        </div>
        <button onClick={saveAll} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="content-save-btn">
          <Save size={14}/> {saving ? "SAVING..." : "SAVE ALL"}
        </button>
      </div>

      <div className="space-y-8">
        {FIELDS.map(group => (
          <div key={group.group} className="card-industrial p-5">
            <h3 className="font-display text-lg mb-4 border-b-2 border-[var(--ink)] pb-2">{group.group}</h3>
            <div className="space-y-4">
              {group.items.map(it => (
                <Field key={it.key} field={it} value={data[it.key]} onChange={v => set(it.key, v)} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={saveAll} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="content-save-btn-bottom">
          <Save size={14}/> {saving ? "SAVING..." : "SAVE ALL"}
        </button>
      </div>
    </div>
  );
}

function Field({ field, value, onChange }) {
  if (field.type === "image") {
    return (
      <div>
        <label className="font-mono text-xs uppercase tracking-wider block mb-2 flex items-center gap-2"><ImageIcon size={12}/> {field.label}</label>
        <div className="flex items-start gap-3">
          {value && (
            <div className="relative">
              <img src={value} alt={field.label} className="w-24 h-24 object-contain border-2 border-[var(--ink)] bg-[var(--paper)]" data-testid={`content-img-preview-${field.key}`}/>
              <button onClick={()=>onChange("")} type="button" className="absolute -top-2 -right-2 bg-[var(--accent)] text-white p-1 border-2 border-[var(--ink)]" aria-label="Clear"><X size={10}/></button>
            </div>
          )}
          <div className="flex-1">
            <label className="btn-outline inline-flex items-center gap-2 cursor-pointer">
              <Upload size={14}/> UPLOAD
              <input type="file" accept="image/*" className="hidden" data-testid={`content-img-upload-${field.key}`}
                onChange={(e)=>{
                  const file = e.target.files[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => onChange(reader.result);
                  reader.readAsDataURL(file);
                }}/>
            </label>
            <input type="text" placeholder="…or paste an image URL" value={value || ""} onChange={e=>onChange(e.target.value)} className="input-field mt-2 text-xs" data-testid={`content-img-url-${field.key}`}/>
          </div>
        </div>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div>
        <label className="font-mono text-xs uppercase tracking-wider block mb-2 flex items-center gap-2"><Type size={12}/> {field.label}</label>
        <textarea value={value || ""} onChange={e=>onChange(e.target.value)} rows={3} className="input-field font-sans" data-testid={`content-field-${field.key}`}/>
      </div>
    );
  }
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-wider block mb-2 flex items-center gap-2"><Type size={12}/> {field.label}</label>
      <input type="text" value={value || ""} onChange={e=>onChange(e.target.value)} className="input-field" data-testid={`content-field-${field.key}`}/>
    </div>
  );
}
